import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const useExitWarning = (isDirty = false) => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [shouldProceed, setShouldProceed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = useRef(location.pathname);

  // ✅ Update current path after successful navigation
  useEffect(() => {
    if (shouldProceed) {
      currentPath.current = location.pathname;
    }
  }, [location.pathname, shouldProceed]);

  // ✅ Intercept link clicks (react-router <Link>, <NavLink>, etc.)
  useEffect(() => {
    const handleClick = (e) => {
      if (shouldProceed) return;

      let target = e.target;
      while (target && target !== document) {
        if (target.tagName === "A" && target.href) {
          const targetUrl = new URL(target.href);
          const targetPath = targetUrl.pathname;

          // Only block navigation if dirty and different route
          if (
            targetPath !== currentPath.current &&
            targetUrl.origin === window.location.origin
          ) {
            if (isDirty) {
              e.preventDefault();
              e.stopPropagation();
              setShowExitPopup(true);
              setPendingNavigation(targetPath);
            }
            return;
          }
        }
        target = target.parentElement;
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [isDirty, shouldProceed]);

  // 🚫 Removed the refresh (beforeunload) warning logic

  // ✅ Warn on browser back
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      if (isDirty && !shouldProceed) {
        window.history.pushState(null, "", window.location.href);
        setShowExitPopup(true);
        setPendingNavigation("back");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, shouldProceed]);

  const handleStay = useCallback(() => {
    setShowExitPopup(false);
    setPendingNavigation(null);
  }, []);

  const handleLeave = useCallback(() => {
    setShowExitPopup(false);
    setShouldProceed(true);

    setTimeout(() => {
      if (pendingNavigation === "back") {
        window.history.back();
      } else if (pendingNavigation) {
        navigate(pendingNavigation);
      }
      setPendingNavigation(null);

      // Reset
      setTimeout(() => setShouldProceed(false), 100);
    }, 50);
  }, [pendingNavigation, navigate]);

  const navigateWithWarning = useCallback((path) => {
    setShowExitPopup(true);
    setPendingNavigation(path);
  }, []);

  const allowNavigation = useCallback(
    (path) => {
      setShouldProceed(true);
      setTimeout(() => {
        navigate(path);
        setTimeout(() => setShouldProceed(false), 100);
      }, 0);
    },
    [navigate]
  );

  return {
    showExitPopup,
    handleStay,
    handleLeave,
    navigateWithWarning,
    allowNavigation,
  };
};
